import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Warning, Build, BuildCircle } from "@mui/icons-material";

interface GlobalMaintenanceConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCurrentlyEnabled: boolean;
  loading: boolean;
}

const GlobalMaintenanceConfirmDialog: React.FC<
  GlobalMaintenanceConfirmDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  isCurrentlyEnabled,
  loading,
}) => {
  const willBeEnabled = !isCurrentlyEnabled;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {willBeEnabled ? (
            <Build sx={{ color: "#f44336", fontSize: 28 }} />
          ) : (
            <BuildCircle sx={{ color: "#4caf50", fontSize: 28 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {willBeEnabled
              ? "Enable Global Under Maintenance?"
              : "Disable Global Under Maintenance?"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
          <Warning
            sx={{ color: willBeEnabled ? "#f44336" : "#4caf50", mt: 0.5 }}
          />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
              {willBeEnabled
                ? "This will enable maintenance mode for:"
                : "This will disable maintenance mode for:"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {willBeEnabled
                ? `All active operators will be put into maintenance mode.`
                : "Only operators that weren't in maintenance before global was enabled will be restored."}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {willBeEnabled
                ? "Players will not be able to play games for any operator while this is enabled."
                : "Operators that were individually removed from maintenance mode will remain the same."}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: willBeEnabled ? "#ffebee" : "#e8f5e9",
            borderRadius: 2,
            border: willBeEnabled ? "1px solid #f44336" : "1px solid #4caf50",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {willBeEnabled
              ? "⚠️ WARNING: This will immediately affect all active operators and their players!"
              : "✅ Players will be able to resume playing once maintenance is disabled."}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : willBeEnabled ? (
              <Build />
            ) : (
              <BuildCircle />
            )
          }
          sx={{
            backgroundColor: willBeEnabled ? "#f44336" : "#4caf50",
            minWidth: 180,
            "&:hover": {
              backgroundColor: willBeEnabled ? "#d32f2f" : "#45a049",
            },
          }}
        >
          {loading
            ? "Processing..."
            : willBeEnabled
            ? "Enable Global Maintenance"
            : "Disable Global Maintenance"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalMaintenanceConfirmDialog;
